/* eslint-disable @typescript-eslint/indent */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-shadow */
import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { of, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, tap } from "rxjs/operators";
import { INPUT } from "../../../constants/components";
import { DeepSet } from "../../../types/@extensions/deep-set";
import { compare } from "../../../utils/values";
import { SearchBarComponent } from "../search-bar.component";
import { SearchFilterOption } from "../search-filter/search-filter.component";

export type SearchInputType = "text" | "dropdown";
export interface SearchInputOption {
  label: string;
  value: string | number; // use string or number type to avoid check side effects.
  auxValue?: string; // aux value is used to replace main value. It's useful when you need to fetch a property of a reference object.
  disabled?: boolean; // used for special cases where option should be displayed in readonly fields
  hidden?: boolean; // used to hide option
}

@Component({
  selector: "app-search-input",
  templateUrl: "./search-input.component.html",
  styleUrls: ["./search-input.component.scss"],
})
export class SearchInputComponent implements AfterViewInit, AfterContentChecked {
  @Input() disabled: boolean;
  @Input() searching: boolean;
  @Input() inputType?: SearchInputType;
  @Input() options?: SearchInputOption[];
  @Input() selectedFilter: SearchFilterOption;
  @Output() changeEvent = new EventEmitter<string>();
  @Output() asyncChangeEvent = new EventEmitter<string>();
  @ViewChild("searchInput") searchInput: ElementRef;
  @Input() searchBar: SearchBarComponent;
  @Input() value: string;
  auxValue: string;
  searchControl: FormControl;
  inMemoryOptions: SearchInputOption[];
  readonly placeholder = "Search";
  private subject: Subject<KeyboardEvent> = new Subject();

  constructor(private router: Router, private changeDetector: ChangeDetectorRef) {}

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  ngAfterViewInit(): void {
    this.applyAsyncOptions();
    this.applySearchFilter();
    this.subject.pipe(debounceTime(INPUT.SELECT.SEARCH.DEBOUNCE_TIME)).subscribe((c) => {
      if (c.key !== "Enter" && this.value?.length > 0) {
        this.changeValue();
      }
    });
  }

  // #region public functions
  onKeyUp(event: KeyboardEvent): void {
    this.subject.next(event);
  }

  changeValue(): void {
    this.auxValue = this.options?.find((opt) => opt.value === this.value)?.auxValue;
    this.changeEvent.emit(this.value);
  }

  isDisabled(): boolean {
    return this.disabled || this.searching;
  }

  isSearching(): boolean {
    return !this.disabled && this.searching;
  }
  // #endregion

  // #region private functions
  private applyAsyncOptions() {
    setTimeout(async () => {
      const queryParams = this.router.parseUrl(this.router.url).queryParams;

      if (this.selectedFilter?.inputType !== "dropdown") {
        return;
      }
      const value: string =
        (this.searchBar?.getValueParam() in (queryParams || {}) && queryParams[this.searchBar?.getValueParam()]) ||
        undefined;

      if (this.selectedFilter?.asyncOptions) {
        this.selectedFilter?.asyncOptions({ queryParams: { name: value } })?.subscribe((options) => {
          this.inMemoryOptions = options?.sort((a, b) => compare(a.value, b.value));
          this.auxValue = this.inMemoryOptions?.find((opt) => opt.value === value)?.auxValue;
          // Add options to dropdown
          this.options = this.inMemoryOptions;
          this.auxValue = this.options?.find((opt) => opt.value === this.value)?.auxValue;
          this.asyncChangeEvent.emit(this.value);
        });
      }
    });
  }

  private applySearchFilter(): void {
    this.searchControl = new FormControl("");

    this.searchControl.valueChanges
      .pipe(
        distinctUntilChanged<string>(),
        filter((value) => value?.length === 0),
      )
      .subscribe((value) => {
        this.inMemoryOptions =
          this.options =
          this.selectedFilter.options =
            this.getOptionWithSelectedValue(this.inMemoryOptions);
      });

    this.searchControl.valueChanges
      .pipe(
        distinctUntilChanged<string>(),
        tap(() => (this.selectedFilter.search.loading = true)),
        debounceTime(INPUT.SELECT.SEARCH.DEBOUNCE_TIME),
        map((value) => {
          if (value?.length < INPUT.SELECT.SEARCH.MIN_LENGTH) {
            return of(this.options);
          }
          return this.selectedFilter.search.callback({ queryParams: { name: value } });
        }),
        switchMap((value) => value),
        tap(() => (this.selectedFilter.search.loading = false)),
        finalize(() => (this.selectedFilter.search.loading = false)),
      )
      .subscribe((options) => {
        if (!this.searchControl.value) {
          return;
        }
        this.options = options?.sort((a, b) => compare(a.value, b.value));
        this.changeValue();
      });
  }

  private getOptionWithSelectedValue(options: SearchInputOption[]) {
    const value = this.value;
    const selectedValue = value ? this.options?.find((opt) => opt.value === value) : undefined;
    this.auxValue = this.options?.find((opt) => opt.value === value)?.auxValue;

    return [...new DeepSet([...(options || []), selectedValue])]
      .filter((opt) => opt)
      .sort((a, b) => compare(a.value, b.value));
  }
  // #endregion
}
